/**
 * The order status and payment chips (O0; token-only since O1b). The label is the pinned map's
 * (orderStatusMap.js: a stored legacy value reads as its canonical state, so `completed` shows
 * "Delivered" and `shipped` "Out for delivery"); the tone names a --status-<tone> family from
 * theme/tokens.js (fg, -bg, -border), so the chip follows the theme. OrderChip.jsx draws them.
 * An unrecognised value still shows as itself, in the neutral tone.
 */
import { canonicalOrderStatus, orderStatusLabel } from './orderStatusMap';

const NEUTRAL = 'expired';

const TONE_BY_STATUS = {
  pending: 'pending',
  confirmed: 'info',
  processing: 'viewed',
  ready: 'sent',
  out_for_delivery: 'negotiating',
  delivered: 'success',
  cancelled: 'error'
};

/** { label, tone, canonical } for a stored status value. */
export function orderStatusBadge(status) {
  const canonical = canonicalOrderStatus(status);
  return {
    canonical,
    tone: (canonical && TONE_BY_STATUS[canonical]) || NEUTRAL,
    label: orderStatusLabel(status)
  };
}

const PAYMENT = {
  unpaid: { tone: 'error', label: 'Unpaid' },
  deposit_paid: { tone: 'warning', label: 'Deposit paid' },
  partially_paid: { tone: 'warning', label: 'Partially paid' },
  paid: { tone: 'success', label: 'Paid' },
  refunded: { tone: 'expired', label: 'Refunded' }
};

/** { label, tone } for a payment_status value. */
export function orderPaymentBadge(status) {
  const key = typeof status === 'string' ? status.trim().toLowerCase() : '';
  return PAYMENT[key] || { tone: NEUTRAL, label: status || '—' };
}

// The statuses that claim money came in. With nothing recorded the pane cannot show Paid / Balance
// due without contradicting the chip, so it says what is known instead.
const PAID_ISH = new Set(['paid', 'deposit_paid', 'partially_paid']);
const recorded = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0;
};

/**
 * "Marked <status label> · no payment amounts recorded" when payment_status is paid-ish and both
 * amount_paid_cents and deposit_amount_cents are zero or missing; otherwise null.
 */
export function orderPaymentUnrecorded(order) {
  if (!order) return null;
  const key = typeof order.payment_status === 'string' ? order.payment_status.trim().toLowerCase() : '';
  if (!PAID_ISH.has(key)) return null;
  if (recorded(order.amount_paid_cents) || recorded(order.deposit_amount_cents)) return null;
  return `Marked ${PAYMENT[key].label.toLowerCase()} · no payment amounts recorded`;
}

const PROCUREMENT = {
  in_stock: { tone: 'success', label: 'In stock' },
  pending: { tone: 'pending', label: 'Needs PO' },
  partially_on_order: { tone: 'negotiating', label: 'Partial PO' },
  on_order: { tone: 'info', label: 'On order' },
  received: { tone: 'sent', label: 'Received' },
  fulfilled: { tone: 'success', label: 'Fulfilled' }
};

/** { label, tone } for an order line's procurement_status. */
export function orderProcurementBadge(status) {
  return PROCUREMENT[status] || { tone: NEUTRAL, label: status || '—' };
}
